// tab/src/components/RoleDirectory/RoleDirectory.tsx
// OnRounds Phase 2 - Role Directory Component

import React, { useState, useEffect, useCallback } from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Card,
  CardHeader,
  Text,
  Badge,
  Avatar,
  Input,
  Dropdown,
  Option,
  Button,
  Tooltip,
  Divider,
} from '@fluentui/react-components';
import {
  Search24Regular,
  Mail24Regular,
  Clock24Regular,
  Person24Regular,
  Filter24Regular,
  ArrowClockwise24Regular,
} from '@fluentui/react-icons';
import { format, formatDistanceToNow } from 'date-fns';
import { useTeam } from '../../context/TeamContext';
import { useUser } from '../../context/UserContext';
import { api } from '../../services/api';
import { Loading } from '../common/Loading';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('24px'),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    ...shorthands.gap('16px'),
  },
  filters: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
    flexWrap: 'wrap',
  },
  searchInput: {
    minWidth: '250px',
  },
  categoryFilter: {
    minWidth: '150px',
  },
  stats: {
    display: 'flex',
    ...shorthands.gap('24px'),
    flexWrap: 'wrap',
  },
  statCard: {
    ...shorthands.padding('16px', '24px'),
    textAlign: 'center',
    minWidth: '120px',
  },
  statValue: {
    fontSize: tokens.fontSizeBase600,
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorBrandForeground1,
  },
  statLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  categorySection: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  categoryBadge: {
    ...shorthands.padding('4px', '12px'),
    ...shorthands.borderRadius('4px'),
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
  },
  roleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    ...shorthands.gap('16px'),
  },
  roleCard: {
    ...shorthands.padding('16px'),
  },
  roleHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  roleName: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  roleColorDot: {
    width: '12px',
    height: '12px',
    ...shorthands.borderRadius('50%'),
  },
  roleStatus: {
    ...shorthands.padding('4px', '8px'),
    ...shorthands.borderRadius('4px'),
    fontSize: tokens.fontSizeBase200,
  },
  statusOccupied: {
    backgroundColor: tokens.colorPaletteGreenBackground2,
    color: tokens.colorPaletteGreenForeground1,
  },
  statusVacant: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground2,
  },
  statusHandover: {
    backgroundColor: tokens.colorPaletteYellowBackground2,
    color: tokens.colorPaletteYellowForeground2,
  },
  roleBody: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
  },
  personInfo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  personDetails: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  shiftInfo: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  actions: {
    display: 'flex',
    ...shorthands.gap('8px'),
    marginTop: '8px',
  },
  emptyState: {
    textAlign: 'center',
    ...shorthands.padding('40px'),
    color: tokens.colorNeutralForeground3,
  },
});

interface RoleEntry {
  roleId: string;
  roleName: string;
  description: string;
  category: string;
  color: string;
  status: 'vacant' | 'occupied' | 'handover_pending';
  userId: string | null;
  userDisplayName: string | null;
  clockOnTime: string | null;
  plannedEndTime: string | null;
  unreadMessageCount: number;
}

const CATEGORY_LABELS: Record<string, string> = {
  medical: 'Medical',
  nursing: 'Nursing',
  allied_health: 'Allied Health',
  admin: 'Administrative',
  support: 'Support',
};

const CATEGORY_COLORS: Record<string, string> = {
  medical: tokens.colorPaletteBlueForeground2,
  nursing: tokens.colorPaletteGreenForeground2,
  allied_health: tokens.colorPalettePurpleForeground2,
  admin: tokens.colorPaletteYellowForeground2,
  support: tokens.colorNeutralForeground2,
};

export const RoleDirectory: React.FC = () => {
  const styles = useStyles();
  const { currentTeam } = useTeam();
  const { user, currentShift } = useUser();

  const [roles, setRoles] = useState<RoleEntry[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<RoleEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Load directory
  const loadDirectory = useCallback(async () => {
    if (!currentTeam) return;

    try {
      setIsLoading(true);
      setError(null);

      const directory = await api.getRoleDirectory(currentTeam.id);
      
      const mappedRoles: RoleEntry[] = directory.roles.map((r) => ({
        roleId: r.roleId,
        roleName: r.roleName,
        description: r.role?.description || '',
        category: r.role?.category || 'support',
        color: r.role?.color || '#6B7280',
        status: r.status,
        userId: r.userId,
        userDisplayName: r.userDisplayName,
        clockOnTime: r.clockOnTime,
        plannedEndTime: r.plannedEndTime,
        unreadMessageCount: r.unreadMessageCount,
      }));

      setRoles(mappedRoles);
      setLastUpdated(new Date());
    } catch (err: any) {
      console.error('Failed to load directory:', err);
      setError(err.message || 'Failed to load directory');
    } finally {
      setIsLoading(false);
    }
  }, [currentTeam]);

  useEffect(() => {
    loadDirectory();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadDirectory, 30000);
    return () => clearInterval(interval);
  }, [loadDirectory]);

  // Filter roles
  useEffect(() => {
    let filtered = [...roles];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.roleName.toLowerCase().includes(term) ||
          r.userDisplayName?.toLowerCase().includes(term) ||
          r.description.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (categoryFilter !== 'all') {
      filtered = filtered.filter((r) => r.category === categoryFilter);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }

    setFilteredRoles(filtered);
  }, [roles, searchTerm, categoryFilter, statusFilter]);

  // Group roles by category
  const groupedRoles = filteredRoles.reduce((acc, role) => {
    const category = role.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(role);
    return acc;
  }, {} as Record<string, RoleEntry[]>);

  // Calculate stats
  const stats = {
    total: roles.length,
    occupied: roles.filter((r) => r.status === 'occupied').length,
    vacant: roles.filter((r) => r.status === 'vacant').length,
    handover: roles.filter((r) => r.status === 'handover_pending').length,
  };

  if (isLoading && roles.length === 0) {
    return <Loading message="Loading role directory..." />;
  }

  return (
    <div className={styles.container}>
      {/* Header and Filters */}
      <div className={styles.header}>
        <div>
          <Text size={500} weight="semibold">Role Directory</Text>
          <Text size={200} style={{ marginLeft: '12px', color: tokens.colorNeutralForeground3 }}>
            Last updated {formatDistanceToNow(lastUpdated, { addSuffix: true })}
          </Text>
        </div>
        
        <div className={styles.filters}>
          <Input
            className={styles.searchInput}
            contentBefore={<Search24Regular />}
            placeholder="Search roles or people..."
            value={searchTerm}
            onChange={(e, data) => setSearchTerm(data.value)}
          />
          
          <Dropdown
            className={styles.categoryFilter}
            placeholder="Category"
            value={categoryFilter === 'all' ? 'All Categories' : CATEGORY_LABELS[categoryFilter]}
            onOptionSelect={(e, data) => setCategoryFilter(data.optionValue || 'all')}
          >
            <Option value="all">All Categories</Option>
            {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
              <Option key={value} value={value}>{label}</Option>
            ))}
          </Dropdown>

          <Dropdown
            placeholder="Status"
            value={
              statusFilter === 'all' ? 'All Statuses' :
              statusFilter === 'occupied' ? 'Occupied' :
              statusFilter === 'vacant' ? 'Vacant' : 'Handover'
            }
            onOptionSelect={(e, data) => setStatusFilter(data.optionValue || 'all')}
          >
            <Option value="all">All Statuses</Option>
            <Option value="occupied">Occupied</Option>
            <Option value="vacant">Vacant</Option>
            <Option value="handover_pending">Handover Pending</Option>
          </Dropdown>

          <Tooltip content="Refresh" relationship="label">
            <Button
              icon={<ArrowClockwise24Regular />}
              appearance="subtle"
              onClick={loadDirectory}
            />
          </Tooltip>
        </div>
      </div>

      {/* Stats */}
      <div className={styles.stats}>
        <Card className={styles.statCard}>
          <Text className={styles.statValue}>{stats.total}</Text>
          <Text className={styles.statLabel}>Total Roles</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={styles.statValue} style={{ color: tokens.colorPaletteGreenForeground1 }}>
            {stats.occupied}
          </Text>
          <Text className={styles.statLabel}>Occupied</Text>
        </Card>
        <Card className={styles.statCard}>
          <Text className={styles.statValue} style={{ color: tokens.colorNeutralForeground2 }}>
            {stats.vacant}
          </Text>
          <Text className={styles.statLabel}>Vacant</Text>
        </Card>
        {stats.handover > 0 && (
          <Card className={styles.statCard}>
            <Text className={styles.statValue} style={{ color: tokens.colorPaletteYellowForeground2 }}>
              {stats.handover}
            </Text>
            <Text className={styles.statLabel}>Handover Pending</Text>
          </Card>
        )}
      </div>

      {/* Role Directory */}
      {Object.entries(groupedRoles).length === 0 ? (
        <div className={styles.emptyState}>
          <Text size={400}>No roles found matching your filters</Text>
        </div>
      ) : (
        Object.entries(groupedRoles).map(([category, categoryRoles]) => (
          <div key={category} className={styles.categorySection}>
            <div className={styles.categoryHeader}>
              <span
                className={styles.categoryBadge}
                style={{ backgroundColor: `${CATEGORY_COLORS[category]}20`, color: CATEGORY_COLORS[category] }}
              >
                {CATEGORY_LABELS[category] || category}
              </span>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                {categoryRoles.length} role{categoryRoles.length !== 1 ? 's' : ''}
              </Text>
            </div>

            <div className={styles.roleGrid}>
              {categoryRoles.map((role) => (
                <Card key={role.roleId} className={styles.roleCard}>
                  <div className={styles.roleHeader}>
                    <div className={styles.roleName}>
                      <span
                        className={styles.roleColorDot}
                        style={{ backgroundColor: role.color }}
                      />
                      <Text weight="semibold">{role.roleName}</Text>
                    </div>
                    <span
                      className={`${styles.roleStatus} ${
                        role.status === 'occupied' ? styles.statusOccupied :
                        role.status === 'handover_pending' ? styles.statusHandover :
                        styles.statusVacant
                      }`}
                    >
                      {role.status === 'occupied' ? 'On Duty' :
                       role.status === 'handover_pending' ? 'Handover' :
                       'Vacant'}
                    </span>
                  </div>

                  <div className={styles.roleBody}>
                    {role.description && (
                      <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                        {role.description}
                      </Text>
                    )}

                    {role.status === 'occupied' && role.userDisplayName && (
                      <>
                        <Divider />
                        <div className={styles.personInfo}>
                          <Avatar name={role.userDisplayName} size={40} />
                          <div className={styles.personDetails}>
                            <Text weight="semibold">{role.userDisplayName}</Text>
                            {role.clockOnTime && (
                              <div className={styles.shiftInfo}>
                                <Clock24Regular style={{ fontSize: '14px' }} />
                                <span>
                                  Since {format(new Date(role.clockOnTime), 'h:mm a')}
                                  {' '}({formatDistanceToNow(new Date(role.clockOnTime))})
                                </span>
                              </div>
                            )}
                          </div>
                          {role.unreadMessageCount > 0 && (
                            <Badge appearance="filled" color="danger">
                              {role.unreadMessageCount}
                            </Badge>
                          )}
                        </div>

                        <div className={styles.actions}>
                          <Button
                            appearance="subtle"
                            icon={<Mail24Regular />}
                            size="small"
                            disabled={!currentShift} // Can only message if clocked on
                          >
                            Message
                          </Button>
                        </div>
                      </>
                    )}

                    {role.status === 'vacant' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: tokens.colorNeutralForeground3 }}>
                        <Person24Regular />
                        <Text size={200}>No one currently assigned</Text>
                      </div>
                    )}

                    {role.status === 'handover_pending' && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: tokens.colorPaletteYellowForeground2 }}>
                        <Clock24Regular />
                        <Text size={200}>Awaiting someone to clock on</Text>
                        {role.unreadMessageCount > 0 && (
                          <Badge appearance="filled" color="warning">
                            {role.unreadMessageCount} pending
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
};
